import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { adminListAllUsers, resendUserInvite, getAllCompaniesForFeatureGroup, updateUserCompany, removeUserCompany, getUserCompany, adminListAllFeatureGroups, addUserToFeatureGroup, updateUserRole, removeUserFromFeatureGroup, adminDeleteUser } from '../services/featureService';
import { logger } from '../utils/logger';
import './AdminUserList.css';

const PAGE_SIZE = 100;

// Component to display company cell for each feature group
const CompanyCell = ({ user, featureGroupId, onCompanyClick }) => {
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadCompany = async () => {
      try {
        setLoading(true);
        const userCompany = await getUserCompany(user.user_id, featureGroupId);
        setCompany(userCompany);
      } catch (err) {
        logger.error('Error loading company:', err);
        setCompany(null);
      } finally {
        setLoading(false);
      }
    };
    loadCompany();
  }, [user.user_id, featureGroupId]);

  if (loading) {
    return <span className="company-loading">Loading...</span>;
  }

  return (
    <button
      type="button"
      className="company-cell-button-small"
      onClick={onCompanyClick}
      title="Click to assign company"
    >
      {company ? (
        <span className="company-badge-small">{company.company_name}</span>
      ) : (
        <span className="no-company-small">No company</span>
      )}
    </button>
  );
};

const AdminUserList = () => {
  const [users, setUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [resending, setResending] = useState(null);
  const [expandedUsers, setExpandedUsers] = useState(new Set());
  const [filter, setFilter] = useState('');
  const [showCompanyPopup, setShowCompanyPopup] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [companyData, setCompanyData] = useState({}); // { featureGroupId: { companies: [], loading, selectedCompanyId } }
  const [savingCompany, setSavingCompany] = useState(false);
  const [selectedFeatureGroupForCompany, setSelectedFeatureGroupForCompany] = useState(null);
  const [openMenuUserId, setOpenMenuUserId] = useState(null);
  const [openSubMenuGroupId, setOpenSubMenuGroupId] = useState(null);
  const menuRefs = useRef({});
  const subMenuRefs = useRef({});
  const [showAddFeatureGroupDialog, setShowAddFeatureGroupDialog] = useState(false);
  const [showEditRoleDialog, setShowEditRoleDialog] = useState(false);
  const [editingRoleGroup, setEditingRoleGroup] = useState(null);
  const [selectedRole, setSelectedRole] = useState('Guest');
  const [featureGroups, setFeatureGroups] = useState([]);
  const [saving, setSaving] = useState(false);
  // New state for simplified dialog
  const [dialogSelectedFeatureGroupId, setDialogSelectedFeatureGroupId] = useState(null);
  const [dialogAction, setDialogAction] = useState(null); // 'add', 'update', or 'remove'
  const [dialogSelectedRole, setDialogSelectedRole] = useState('Guest');

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await adminListAllUsers(page, PAGE_SIZE);
      setAllUsers(data);
      setHasMore(data.length === PAGE_SIZE);
    } catch (err) {
      setError('Failed to load users');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page]);

  // Filter users based on filter text
  useEffect(() => {
    if (!filter.trim()) {
      setUsers(allUsers);
      return;
    }

    const filterLower = filter.toLowerCase();
    const filtered = allUsers.filter(user => {
      const nameMatch = (user.full_name || '').toLowerCase().includes(filterLower);
      const emailMatch = (user.email || '').toLowerCase().includes(filterLower);
      const companyMatch = user.companies && user.companies.some(company => 
        (company.company_name || '').toLowerCase().includes(filterLower)
      );
      return nameMatch || emailMatch || companyMatch;
    });
    setUsers(filtered);
  }, [filter, allUsers]);

  useEffect(() => {
    loadUsers();
    loadFeatureGroups();
  }, [loadUsers]);

  const loadFeatureGroups = async () => {
    try {
      const data = await adminListAllFeatureGroups();
      setFeatureGroups(data);
    } catch (err) {
      logger.error('Error loading feature groups:', err);
    }
  };

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Don't close if clicking on menu buttons, menu items, or submenu items
      if (event.target.closest('.menu-button') || 
          event.target.closest('.submenu-button') ||
          event.target.closest('.menu-item') || 
          event.target.closest('.submenu-item') ||
          event.target.closest('.menu-dropdown') ||
          event.target.closest('.submenu-dropdown')) {
        return;
      }

      // Check main menus
      Object.values(menuRefs.current).forEach((ref) => {
        if (ref && !ref.contains(event.target)) {
          setOpenMenuUserId(null);
        }
      });
      // Check sub menus
      Object.values(subMenuRefs.current).forEach((ref) => {
        if (ref && !ref.contains(event.target)) {
          setOpenSubMenuGroupId(null);
        }
      });
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  const handleResendInvite = async (email) => {
    try {
      setResending(email);
      await resendUserInvite(email);
      alert(`Invite resent to ${email}`);
      setOpenMenuUserId(null);
    } catch (err) {
      alert(`Failed to resend invite: ${err.message}`);
      console.error(err);
    } finally {
      setResending(null);
    }
  };

  const handleCopyUserId = async (userId) => {
    try {
      await navigator.clipboard.writeText(userId);
      alert('User ID copied to clipboard');
      setOpenMenuUserId(null);
    } catch (err) {
      alert('Failed to copy user ID');
      logger.error(err);
    }
  };

  const handleAddFeatureGroup = (user) => {
    setSelectedUser(user);
    setDialogSelectedFeatureGroupId(null);
    setDialogAction(null);
    setDialogSelectedRole('Guest');
    setShowAddFeatureGroupDialog(true);
    setOpenMenuUserId(null);
  };

  const handleSaveFeatureGroupChanges = async () => {
    if (!selectedUser || !dialogSelectedFeatureGroupId || !dialogAction) return;

    try {
      setSaving(true);
      
      if (dialogAction === 'remove') {
        await removeUserFromFeatureGroup(selectedUser.user_id, dialogSelectedFeatureGroupId);
      } else if (dialogAction === 'add') {
        if (!dialogSelectedRole) {
          alert('Please select a role');
          return;
        }
        await addUserToFeatureGroup(selectedUser.user_id, dialogSelectedFeatureGroupId, dialogSelectedRole);
      } else if (dialogAction === 'update') {
        if (!dialogSelectedRole) {
          alert('Please select a role');
          return;
        }
        await updateUserRole(selectedUser.user_id, dialogSelectedFeatureGroupId, dialogSelectedRole);
      }

      await loadUsers();
      setShowAddFeatureGroupDialog(false);
      setSelectedUser(null);
      setDialogSelectedFeatureGroupId(null);
      setDialogAction(null);
      setDialogSelectedRole('Guest');
    } catch (err) {
      alert(`Failed to update feature group: ${err.message}`);
      logger.error(err);
    } finally {
      setSaving(false);
    }
  };


  const handleDeleteUser = async (user) => {
    if (!window.confirm(`Are you sure you want to delete user ${user.full_name || user.email}? This will permanently delete the user from all tables and cannot be undone.`)) {
      return;
    }

    try {
      setSaving(true);
      await adminDeleteUser(user.user_id);
      await loadUsers();
      setOpenMenuUserId(null);
    } catch (err) {
      alert(`Failed to delete user: ${err.message}`);
      logger.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleEditRole = (user, group) => {
    setSelectedUser(user);
    setEditingRoleGroup(group);
    setSelectedRole(group.role);
    setShowEditRoleDialog(true);
    setOpenSubMenuGroupId(null);
  };

  const handleSaveEditRole = async () => {
    if (!selectedUser || !editingRoleGroup) return;

    try {
      setSaving(true);
      await updateUserRole(selectedUser.user_id, editingRoleGroup.feature_group_id, selectedRole);
      await loadUsers();
      setShowEditRoleDialog(false);
      setSelectedUser(null);
      setEditingRoleGroup(null);
    } catch (err) {
      alert(`Failed to update role: ${err.message}`);
      logger.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteFeatureMap = async (user, group) => {
    if (!window.confirm(`Are you sure you want to remove ${user.full_name || user.email} from ${group.feature_group_name}?`)) {
      return;
    }

    try {
      setSaving(true);
      await removeUserFromFeatureGroup(user.user_id, group.feature_group_id);
      await loadUsers();
      setOpenSubMenuGroupId(null);
    } catch (err) {
      alert(`Failed to remove user from feature group: ${err.message}`);
      logger.error(err);
    } finally {
      setSaving(false);
    }
  };

  // Get user's current role in each feature group
  const getUserRoleInGroup = (featureGroupId) => {
    if (!selectedUser?.feature_groups) return null;
    const membership = selectedUser.feature_groups.find(
      group => group.feature_group_id === featureGroupId
    );
    return membership?.role || null;
  };


  const handlePrevPage = () => {
    if (page > 0) {
      setPage(page - 1);
      setExpandedUsers(new Set()); // Clear expanded rows on page change
    }
  };

  const handleNextPage = () => {
    if (hasMore) {
      setPage(page + 1);
      setExpandedUsers(new Set()); // Clear expanded rows on page change
    }
  };

  const toggleExpand = (userId) => {
    const newExpanded = new Set(expandedUsers);
    if (newExpanded.has(userId)) {
      newExpanded.delete(userId);
    } else {
      newExpanded.add(userId);
    }
    setExpandedUsers(newExpanded);
  };

  const handleCompanyClick = async (user, specificFeatureGroupId = null) => {
    setSelectedUser(user);
    setShowCompanyPopup(true);
    setCompanyData({});
    setSelectedFeatureGroupForCompany(specificFeatureGroupId);

    // If a specific feature group is provided, only show that one
    const groupsToLoad = specificFeatureGroupId 
      ? user.feature_groups.filter(g => g.feature_group_id === specificFeatureGroupId)
      : user.feature_groups;

    // Load companies for each feature group
    if (groupsToLoad && groupsToLoad.length > 0) {
      const newCompanyData = {};
      
      await Promise.all(
        groupsToLoad.map(async (group) => {
          const featureGroupId = group.feature_group_id;
          newCompanyData[featureGroupId] = {
            companies: [],
            loading: true,
            selectedCompanyId: null,
            featureGroupName: group.feature_group_name
          };

          try {
            // Load companies and current user company in parallel
            const [companies, currentUserCompany] = await Promise.all([
              getAllCompaniesForFeatureGroup(featureGroupId),
              getUserCompany(user.user_id, featureGroupId)
            ]);
            
            newCompanyData[featureGroupId] = {
              companies,
              loading: false,
              selectedCompanyId: currentUserCompany?.company_id || null,
              featureGroupName: group.feature_group_name
            };
          } catch (err) {
            logger.error(`Error loading companies for feature group ${featureGroupId}:`, err);
            newCompanyData[featureGroupId] = {
              companies: [],
              loading: false,
              selectedCompanyId: null,
              featureGroupName: group.feature_group_name,
              error: 'Failed to load companies'
            };
          }
        })
      );

      setCompanyData(newCompanyData);
    }
  };


  const handleCloseCompanyPopup = () => {
    setShowCompanyPopup(false);
    setSelectedUser(null);
    setCompanyData({});
    setSelectedFeatureGroupForCompany(null);
  };

  const handleCompanySelect = (featureGroupId, companyId) => {
    setCompanyData(prev => ({
      ...prev,
      [featureGroupId]: {
        ...prev[featureGroupId],
        selectedCompanyId: companyId
      }
    }));
  };

  const handleSaveCompanies = async () => {
    if (!selectedUser) return;

    try {
      setSavingCompany(true);
      
      // Update company for each feature group
      await Promise.all(
        Object.entries(companyData).map(async ([featureGroupId, data]) => {
          const userId = selectedUser.user_id;
          
          if (data.selectedCompanyId) {
            // Update or create user company mapping
            await updateUserCompany(userId, data.selectedCompanyId, featureGroupId);
          } else {
            // Remove company mapping if no company selected
            await removeUserCompany(userId, featureGroupId);
          }
        })
      );

      // Reload users to reflect changes
      await loadUsers();
      handleCloseCompanyPopup();
    } catch (err) {
      logger.error('Error saving user companies:', err);
      alert('Failed to save company assignments. Please try again.');
    } finally {
      setSavingCompany(false);
    }
  };

  return (
    <div className="admin-users-section">
      <div className="admin-users-header">
        <h3>All Users</h3>
        <div className="pagination-controls">
          <button
            type="button"
            className="pagination-button"
            onClick={handlePrevPage}
            disabled={page === 0 || loading}
          >
            Prev
          </button>
          <span className="pagination-info">Page {page + 1}</span>
          <button
            type="button"
            className="pagination-button"
            onClick={handleNextPage}
            disabled={!hasMore || loading}
          >
            Next
          </button>
        </div>
      </div>

      {loading && <div className="admin-users-loading">Loading users...</div>}
      {error && <div className="admin-users-error">{error}</div>}

      {!loading && !error && (
        <div className="admin-users-table-wrapper">
          <div className="admin-users-filter">
            <input
              type="text"
              className="filter-input"
              placeholder="Filter by name, email, or company..."
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
          </div>
          {users.length === 0 ? (
            <div className="admin-users-empty">
              {filter ? 'No users match your filter.' : 'No users found.'}
            </div>
          ) : (
            <table className="admin-users-table">
              <thead>
                <tr>
                  <th></th>
                  <th>User</th>
                  <th>Email</th>
                  <th>Last Login</th>
                  <th>Invite Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => {
                  const isExpanded = expandedUsers.has(user.user_id);
                  const hasFeatureGroups = user.feature_groups && user.feature_groups.length > 0;
                  
                  return (
                    <React.Fragment key={user.user_id}>
                      <tr className="admin-user-row">
                        <td className="expand-cell">
                          {hasFeatureGroups && (
                            <button
                              type="button"
                              className="expand-button"
                              onClick={() => toggleExpand(user.user_id)}
                              title={isExpanded ? 'Collapse' : 'Expand'}
                            >
                              <i className={`fas fa-chevron-${isExpanded ? 'down' : 'right'}`}></i>
                            </button>
                          )}
                        </td>
                        <td className="admin-user-name">{user.full_name || 'N/A'}</td>
                        <td className="admin-user-email">{user.email || 'N/A'}</td>
                        <td className="admin-user-login">
                          {user.last_login ? new Date(user.last_login).toLocaleString() : 'Never'}
                        </td>
                        <td className="admin-user-status">{user.invite_status}</td>
                        <td className="admin-user-actions">
                          <div className="menu-container" ref={el => menuRefs.current[user.user_id] = el}>
                            <button
                              type="button"
                              className="menu-button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setOpenMenuUserId(openMenuUserId === user.user_id ? null : user.user_id);
                              }}
                            >
                              <i className="fas fa-ellipsis-v"></i>
                            </button>
                            {openMenuUserId === user.user_id && (
                              <div className="menu-dropdown">
                                <button
                                  type="button"
                                  className="menu-item"
                                  onClick={() => handleResendInvite(user.email)}
                                  disabled={user.invite_status === 'Accepted' || resending === user.email}
                                >
                                  <i className="fas fa-paper-plane"></i>
                                  {resending === user.email ? 'Sending...' : 'Resend Invite'}
                                </button>
                                <button
                                  type="button"
                                  className="menu-item"
                                  onClick={() => handleCopyUserId(user.user_id)}
                                >
                                  <i className="fas fa-copy"></i>
                                  Copy User ID
                                </button>
                                <button
                                  type="button"
                                  className="menu-item"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleAddFeatureGroup(user);
                                  }}
                                >
                                  <i className="fas fa-edit"></i>
                                  Update Feature Maps
                                </button>
                                <div className="menu-divider"></div>
                                <button
                                  type="button"
                                  className="menu-item menu-item-danger"
                                  onClick={() => handleDeleteUser(user)}
                                  disabled={saving}
                                >
                                  <i className="fas fa-trash"></i>
                                  Delete User
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                      {isExpanded && hasFeatureGroups && (
                        <tr className="admin-user-detail-row">
                          <td colSpan="6" className="detail-cell">
                            <div className="feature-groups-detail">
                              <ul className="feature-groups-list">
                                {user.feature_groups.map((group, index) => {
                                  const menuKey = `${user.user_id}-${group.feature_group_id}`;
                                  return (
                                    <li key={`${group.feature_group_id}-${index}`}>
                                      <span className="group-name">{group.feature_group_name}</span>
                                      <span className={`role-badge role-${group.role.toLowerCase()}`}>{group.role}</span>
                                      <span className="group-company">
                                        <CompanyCell 
                                          key={`${user.user_id}-${group.feature_group_id}`}
                                          user={user} 
                                          featureGroupId={group.feature_group_id}
                                          onCompanyClick={() => handleCompanyClick(user, group.feature_group_id)}
                                        />
                                      </span>
                                      {group.created_at && (
                                        <span className="group-joined">
                                          Joined: {new Date(group.created_at).toLocaleDateString()}
                                        </span>
                                      )}
                                      <div className="submenu-container" ref={el => subMenuRefs.current[menuKey] = el}>
                                        <button
                                          type="button"
                                          className="submenu-button"
                                          onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            setOpenSubMenuGroupId(openSubMenuGroupId === menuKey ? null : menuKey);
                                          }}
                                        >
                                          <i className="fas fa-ellipsis-v"></i>
                                        </button>
                                        {openSubMenuGroupId === menuKey && (
                                          <div className="submenu-dropdown">
                                            <button
                                              type="button"
                                              className="submenu-item"
                                              onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                handleEditRole(user, group);
                                              }}
                                            >
                                              <i className="fas fa-edit"></i>
                                              Edit Role
                                            </button>
                                            <div className="menu-divider"></div>
                                            <button
                                              type="button"
                                              className="submenu-item submenu-item-danger"
                                              onClick={() => handleDeleteFeatureMap(user, group)}
                                              disabled={saving}
                                            >
                                              <i className="fas fa-trash"></i>
                                              Disconnect Feature Map
                                            </button>
                                          </div>
                                        )}
                                      </div>
                                    </li>
                                  );
                                })}
                              </ul>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {showCompanyPopup && selectedUser && (
        <div className="company-popup-overlay" onClick={handleCloseCompanyPopup}>
          <div className="company-popup" onClick={(e) => e.stopPropagation()}>
            <div className="company-popup-header">
              <h3>
                Assign Company for {selectedUser.full_name || selectedUser.email}
                {selectedFeatureGroupForCompany && companyData[selectedFeatureGroupForCompany] && (
                  <span className="popup-subtitle"> - {companyData[selectedFeatureGroupForCompany].featureGroupName}</span>
                )}
              </h3>
              <button className="popup-close-button" onClick={handleCloseCompanyPopup}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="company-popup-content">
              {selectedUser.feature_groups && selectedUser.feature_groups.length > 0 ? (
                <div className="feature-groups-companies">
                  {selectedUser.feature_groups.map((group) => {
                    const featureGroupId = group.feature_group_id;
                    const data = companyData[featureGroupId];
                    
                    if (!data) {
                      return (
                        <div key={featureGroupId} className="company-group-section">
                          <h4>{group.feature_group_name}</h4>
                          <div className="loading-companies">Loading companies...</div>
                        </div>
                      );
                    }

                    return (
                      <div key={featureGroupId} className="company-group-section">
                        <h4>{data.featureGroupName || group.feature_group_name}</h4>
                        {data.loading ? (
                          <div className="loading-companies">Loading companies...</div>
                        ) : data.error ? (
                          <div className="error-companies">{data.error}</div>
                        ) : data.companies.length > 0 ? (
                          <div className="companies-select-list">
                            <label className="company-option">
                              <input
                                type="radio"
                                name={`company-${featureGroupId}`}
                                checked={!data.selectedCompanyId}
                                onChange={() => handleCompanySelect(featureGroupId, null)}
                              />
                              <span>No company</span>
                            </label>
                            {data.companies.map((company) => (
                              <label key={company.id} className="company-option">
                                <input
                                  type="radio"
                                  name={`company-${featureGroupId}`}
                                  value={company.id}
                                  checked={data.selectedCompanyId === company.id}
                                  onChange={() => handleCompanySelect(featureGroupId, company.id)}
                                />
                                <span>{company.name}</span>
                              </label>
                            ))}
                          </div>
                        ) : (
                          <div className="no-companies-available">No companies available for this feature group</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="no-feature-groups">
                  User is not a member of any feature groups.
                </div>
              )}
            </div>
            <div className="company-popup-actions">
              <button
                type="button"
                className="popup-cancel-button"
                onClick={handleCloseCompanyPopup}
                disabled={savingCompany}
              >
                Cancel
              </button>
              <button
                type="button"
                className="popup-save-button"
                onClick={handleSaveCompanies}
                disabled={savingCompany}
              >
                {savingCompany ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Update Feature Maps Dialog */}
      {showAddFeatureGroupDialog && selectedUser && createPortal(
        <div className="dialog-overlay" onClick={() => setShowAddFeatureGroupDialog(false)} style={{ zIndex: 9999 }}>
          <div className="dialog" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-header">
              <h3>Update Feature Maps for {selectedUser?.full_name || selectedUser?.email || 'User'}</h3>
              <button className="dialog-close" onClick={() => setShowAddFeatureGroupDialog(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="dialog-content">
              {/* Step 1: Choose Feature Map */}
              <div className="form-group">
                <label>1. Choose Feature Map</label>
                <select
                  className="form-select"
                  value={dialogSelectedFeatureGroupId || ''}
                  onChange={(e) => {
                    const fgId = e.target.value;
                    setDialogSelectedFeatureGroupId(fgId || null);
                    setDialogAction(null); // Reset action when feature group changes
                    if (fgId) {
                      const currentRole = getUserRoleInGroup(fgId);
                      if (currentRole) {
                        setDialogSelectedRole(currentRole);
                      } else {
                        setDialogSelectedRole('Guest');
                      }
                    }
                  }}
                >
                  <option value="">-- Select a feature map --</option>
                  {featureGroups.map((fg) => (
                    <option key={fg.feature_group_id} value={fg.feature_group_id}>
                      {fg.display_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Step 2: Choose Action (only show if feature group is selected) */}
              {dialogSelectedFeatureGroupId && (
                <div className="form-group">
                  <label>2. Choose Action</label>
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    {(() => {
                      const currentRole = getUserRoleInGroup(dialogSelectedFeatureGroupId);
                      const isMember = !!currentRole;
                      
                      if (isMember) {
                        return (
                          <>
                            <button
                              type="button"
                              className={`dialog-button ${dialogAction === 'update' ? 'dialog-button-primary' : 'dialog-button-secondary'}`}
                              onClick={() => {
                                setDialogAction('update');
                                setDialogSelectedRole(currentRole);
                              }}
                            >
                              Update Feature Group
                            </button>
                            <button
                              type="button"
                              className={`dialog-button ${dialogAction === 'remove' ? 'dialog-button-danger' : 'dialog-button-secondary'}`}
                              onClick={() => setDialogAction('remove')}
                            >
                              Remove Feature Group
                            </button>
                          </>
                        );
                      } else {
                        return (
                          <button
                            type="button"
                            className={`dialog-button ${dialogAction === 'add' ? 'dialog-button-primary' : 'dialog-button-secondary'}`}
                            onClick={() => {
                              setDialogAction('add');
                              setDialogSelectedRole('Guest');
                            }}
                          >
                            Add Feature Group
                          </button>
                        );
                      }
                    })()}
                  </div>
                </div>
              )}

              {/* Step 3: Choose Role (only show if Add or Update action is selected) */}
              {(dialogAction === 'add' || dialogAction === 'update') && dialogSelectedFeatureGroupId && (
                <div className="form-group">
                  <label>3. Choose Role</label>
                  <select
                    className="form-select"
                    value={dialogSelectedRole}
                    onChange={(e) => setDialogSelectedRole(e.target.value)}
                  >
                    <option value="Guest">Guest</option>
                    <option value="Editor">Editor</option>
                    <option value="Owner">Owner</option>
                  </select>
                  {(() => {
                    const currentRole = getUserRoleInGroup(dialogSelectedFeatureGroupId);
                    if (currentRole && dialogAction === 'update') {
                      return (
                        <div style={{ marginTop: '8px', fontSize: '0.9em', color: '#666' }}>
                          Current role: <strong>{currentRole}</strong>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
              )}
            </div>
            <div className="dialog-actions">
              <button
                type="button"
                className="dialog-button dialog-button-cancel"
                onClick={() => {
                  setShowAddFeatureGroupDialog(false);
                  setDialogSelectedFeatureGroupId(null);
                  setDialogAction(null);
                  setDialogSelectedRole('Guest');
                }}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="dialog-button dialog-button-primary"
                onClick={handleSaveFeatureGroupChanges}
                disabled={saving || !dialogSelectedFeatureGroupId || !dialogAction || (dialogAction !== 'remove' && !dialogSelectedRole)}
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Edit Role Dialog */}
      {showEditRoleDialog && selectedUser && editingRoleGroup && createPortal(
        <div className="dialog-overlay" onClick={() => setShowEditRoleDialog(false)} style={{ zIndex: 9999 }}>
          <div className="dialog" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-header">
              <h3>Edit Role for {editingRoleGroup.feature_group_name}</h3>
              <button className="dialog-close" onClick={() => setShowEditRoleDialog(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="dialog-content">
              <div className="form-group">
                <label>User</label>
                <div className="form-readonly">{selectedUser.full_name || selectedUser.email}</div>
              </div>
              <div className="form-group">
                <label>Feature Group</label>
                <div className="form-readonly">{editingRoleGroup.feature_group_name}</div>
              </div>
              <div className="form-group">
                <label>Role</label>
                <select
                  className="form-select"
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value)}
                >
                  <option value="Guest">Guest</option>
                  <option value="Editor">Editor</option>
                  <option value="Owner">Owner</option>
                </select>
              </div>
            </div>
            <div className="dialog-actions">
              <button
                type="button"
                className="dialog-button dialog-button-cancel"
                onClick={() => setShowEditRoleDialog(false)}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="dialog-button dialog-button-primary"
                onClick={handleSaveEditRole}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default AdminUserList;
